import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QrCode, Upload, X, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useForm } from '../../hooks/useForm';
import { useToast } from '../Toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import TextArea from '../ui/Textarea';
import Modal from '../Modal';
import SearchInput from '../ui/SearchInput';
import equipamentosService, { estadosFisicos } from '../../services/equipamentos';
import clientesService from '../../services/clientes';
const EquipamentoForm = ({ equipamento, clienteId, onSuccess, onCancel }) => {
    const { success, error } = useToast();
    const queryClient = useQueryClient();
    // Estados locais
    const [selectedTipoEquipamento, setSelectedTipoEquipamento] = useState(equipamento?.modelo.tipoEquipamentoId);
    const [selectedMarca, setSelectedMarca] = useState(equipamento?.modelo.marcaId);
    // Função para construir URL da imagem (relativa para aproveitar proxy do Vite)
    const buildImageUrl = (url) => {
        if (url.startsWith('http'))
            return url;
        // Usar URL relativa que será proxy pelo Vite
        return url.startsWith('/uploads') ? url : `/uploads${url}`;
    };
    const [fotos, setFotos] = useState(() => {
        if (!equipamento?.fotos)
            return [];
        try {
            // Se fotos é uma string JSON, fazer parse
            if (typeof equipamento.fotos === 'string') {
                const fotosArray = JSON.parse(equipamento.fotos);
                return Array.isArray(fotosArray) ? fotosArray.map(buildImageUrl) : [];
            }
            // Se já é array
            if (Array.isArray(equipamento.fotos)) {
                return equipamento.fotos.map(buildImageUrl);
            }
        }
        catch (error) {
            console.error('Erro ao processar fotos do equipamento:', error);
        }
        return [];
    });
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [modalAberto, setModalAberto] = useState(null);
    const [novoItemNome, setNovoItemNome] = useState('');
    const [fotoSelecionada, setFotoSelecionada] = useState(null);
    const [indiceFotoAtual, setIndiceFotoAtual] = useState(0);
    // Estados para os campos de busca
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [selectedTipo, setSelectedTipo] = useState(null);
    const [selectedMarcaObj, setSelectedMarcaObj] = useState(null);
    const [selectedModelo, setSelectedModelo] = useState(null);
    // Queries
    const { data: clientes } = useQuery({
        queryKey: ['clientes', 'all'],
        queryFn: () => clientesService.getClientes({ limit: 1000 }),
        select: (data) => data.data
    });
    const { data: tiposEquipamento } = useQuery({
        queryKey: ['tipos-equipamento'],
        queryFn: () => equipamentosService.getTiposEquipamento()
    });
    const { data: marcas } = useQuery({
        queryKey: ['marcas'],
        queryFn: () => equipamentosService.getMarcas()
    });
    const { data: modelos } = useQuery({
        queryKey: ['modelos', selectedTipoEquipamento, selectedMarca],
        queryFn: () => equipamentosService.getModelosByMarcaAndTipo(selectedMarca, selectedTipoEquipamento),
        enabled: !!(selectedTipoEquipamento && selectedMarca)
    });
    // Mutations
    const createTipoMutation = useMutation({
        mutationFn: (nome) => equipamentosService.createTipoEquipamento(nome),
        onSuccess: (data) => {
            success('Tipo de equipamento criado com sucesso');
            queryClient.invalidateQueries({ queryKey: ['tipos-equipamento'] });
            setSelectedTipoEquipamento(data.id);
            setSelectedTipo(data); // Para aparecer no SearchInput
            setSelectedMarcaObj(null); // Limpar marca e modelo selecionados
            setSelectedMarca(undefined);
            setSelectedModelo(null);
            setFieldValue('modeloId', '');
            setModalAberto(null);
            setNovoItemNome('');
        },
        onError: (err) => {
            error('Erro ao criar tipo', err.response?.data?.error || err.message);
        },
    });
    const createMarcaMutation = useMutation({
        mutationFn: (nome) => equipamentosService.createMarca(nome),
        onSuccess: (data) => {
            success('Marca criada com sucesso');
            queryClient.invalidateQueries({ queryKey: ['marcas'] });
            setSelectedMarca(data.id);
            setSelectedMarcaObj(data); // Para aparecer no SearchInput
            setSelectedModelo(null); // Limpar modelo selecionado
            setFieldValue('modeloId', '');
            setModalAberto(null);
            setNovoItemNome('');
        },
        onError: (err) => {
            error('Erro ao criar marca', err.response?.data?.error || err.message);
        },
    });
    const createModeloMutation = useMutation({
        mutationFn: (data) => equipamentosService.createModelo(data),
        onSuccess: (data) => {
            success('Modelo criado com sucesso');
            queryClient.invalidateQueries({ queryKey: ['modelos', selectedTipoEquipamento, selectedMarca] });
            setSelectedModelo(data); // Para aparecer no SearchInput
            setFieldValue('modeloId', data.id.toString());
            setModalAberto(null);
            setNovoItemNome('');
        },
        onError: (err) => {
            error('Erro ao criar modelo', err.response?.data?.error || err.message);
        },
    });
    const uploadPhotoMutation = useMutation({
        mutationFn: (file) => equipamentosService.uploadFoto(file),
        onSuccess: (data) => {
            const imageUrl = buildImageUrl(data.url);
            setFotos(prev => [...prev, imageUrl]);
            success('Foto enviada com sucesso');
        },
        onError: (err) => {
            error('Erro ao enviar foto', err.response?.data?.error || err.message);
        },
        onSettled: () => {
            setUploadingPhoto(false);
        }
    });
    // Funções de busca para autocomplete
    const searchClientes = async (term) => {
        try {
            const response = await clientesService.getClientes({ search: term, limit: 10 });
            return response.data.map(cliente => ({
                id: cliente.id,
                nome: cliente.nome,
                subtitle: cliente.telefonePrincipal || cliente.email || 'Sem contacto',
                tipo: 'Cliente'
            }));
        }
        catch (error) {
            console.error('Erro ao buscar clientes:', error);
            return [];
        }
    };
    const searchTipos = async (term) => {
        try {
            const tipos = await equipamentosService.getTiposEquipamento();
            return tipos
                .filter(tipo => tipo.nome.toLowerCase().includes(term.toLowerCase()))
                .map(tipo => ({
                id: tipo.id,
                nome: tipo.nome,
                tipo: 'Tipo'
            }))
                .slice(0, 10);
        }
        catch (error) {
            console.error('Erro ao buscar tipos:', error);
            return [];
        }
    };
    const searchMarcas = async (term) => {
        try {
            const marcasData = await equipamentosService.getMarcas();
            return marcasData
                .filter(marca => marca.nome.toLowerCase().includes(term.toLowerCase()))
                .map(marca => ({
                id: marca.id,
                nome: marca.nome,
                tipo: 'Marca'
            }))
                .slice(0, 10);
        }
        catch (error) {
            console.error('Erro ao buscar marcas:', error);
            return [];
        }
    };
    const searchModelos = async (term) => {
        try {
            const modelos = await equipamentosService.getModelosByMarcaAndTipo(selectedMarcaObj?.id, selectedTipo?.id);
            return modelos
                .filter(modelo => modelo.nome.toLowerCase().includes(term.toLowerCase()))
                .map(modelo => ({
                id: modelo.id,
                nome: modelo.nome,
                tipo: 'Modelo'
            }))
                .slice(0, 10);
        }
        catch (error) {
            console.error('Erro ao buscar modelos:', error);
            return [];
        }
    };
    // Form
    const { values, errors, isSubmitting, handleChange, handleBlur, handleSubmit, getFieldProps, setFieldValue } = useForm({
        initialValues: {
            clienteId: clienteId || equipamento?.clienteId || '',
            modeloId: equipamento?.modeloId || '',
            numeroSerie: equipamento?.numeroSerie || '',
            imei: equipamento?.imei || '',
            cor: equipamento?.cor || '',
            estadoFisico: equipamento?.estadoFisico || 'BOM',
            observacoes: equipamento?.observacoes || ''
        },
        validationRules: {
            clienteId: {
                required: 'Cliente é obrigatório',
                transform: (value) => value ? parseInt(value) : undefined
            },
            modeloId: {
                required: 'Modelo é obrigatório',
                transform: (value) => value ? parseInt(value) : undefined
            },
            estadoFisico: {
                required: 'Estado físico é obrigatório'
            }
        },
        onSubmit: async (formValues) => {
            try {
                const data = {
                    ...formValues,
                    clienteId: parseInt(formValues.clienteId, 10),
                    modeloId: parseInt(formValues.modeloId, 10),
                    fotos: fotos.length > 0 ? fotos : undefined
                };
                if (equipamento) {
                    await equipamentosService.update(equipamento.id, data);
                    success('Equipamento atualizado com sucesso');
                }
                else {
                    await equipamentosService.create(data);
                    success('Equipamento criado com sucesso');
                }
                onSuccess();
            }
            catch (err) {
                error('Erro ao salvar equipamento', err.response?.data?.error || err.message);
            }
        }
    });
    // Efeitos
    useEffect(() => {
        if (equipamento) {
            // Definir tipo e marca baseado no modelo do equipamento
            setSelectedTipoEquipamento(equipamento.modelo.tipoEquipamentoId);
            setSelectedMarca(equipamento.modelo.marcaId);
        }
    }, [equipamento]);
    // Carregar cliente quando clienteId for passado como prop
    useEffect(() => {
        if (clienteId) {
            // Primeiro tentar buscar o cliente nos dados já carregados
            if (clientes) {
                const cliente = clientes.find((c) => c.id === clienteId);
                if (cliente) {
                    setSelectedCliente(cliente);
                    setFieldValue('clienteId', cliente.id.toString());
                }
            }
            else {
                // Se não temos a lista de clientes, buscar o cliente específico
                clientesService.getClienteById(clienteId).then((cliente) => {
                    setSelectedCliente(cliente);
                    setFieldValue('clienteId', cliente.id.toString());
                }).catch((err) => {
                    console.error('Erro ao carregar cliente:', err);
                });
            }
        }
    }, [clienteId, clientes, setFieldValue]);
    // Keyboard navigation for photo modal
    useEffect(() => {
        if (fotoSelecionada) {
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [fotoSelecionada]);
    // Handlers
    const handleTipoEquipamentoChange = (event) => {
        const value = event.target.value;
        const tipoId = value ? parseInt(value) : undefined;
        setSelectedTipoEquipamento(tipoId);
        setSelectedMarca(undefined);
        setFieldValue('modeloId', '');
    };
    const handleMarcaChange = (event) => {
        const value = event.target.value;
        const marcaId = value ? parseInt(value) : undefined;
        setSelectedMarca(marcaId);
        setFieldValue('modeloId', '');
    };
    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB
                error('Foto muito grande', 'O arquivo deve ter no máximo 5MB');
                return;
            }
            if (!file.type.startsWith('image/')) {
                error('Formato inválido', 'Apenas imagens são permitidas');
                return;
            }
            setUploadingPhoto(true);
            uploadPhotoMutation.mutate(file);
        }
    };
    const handleRemovePhoto = (index) => {
        setFotos(prev => prev.filter((_, i) => i !== index));
    };
    const handleOpenPhoto = (foto, index) => {
        setFotoSelecionada(foto);
        setIndiceFotoAtual(index);
    };
    const handleClosePhoto = () => {
        setFotoSelecionada(null);
        setIndiceFotoAtual(0);
    };
    const handlePreviousPhoto = () => {
        if (fotos.length > 1) {
            const novoIndice = indiceFotoAtual > 0 ? indiceFotoAtual - 1 : fotos.length - 1;
            setIndiceFotoAtual(novoIndice);
            setFotoSelecionada(fotos[novoIndice]);
        }
    };
    const handleNextPhoto = () => {
        if (fotos.length > 1) {
            const novoIndice = indiceFotoAtual < fotos.length - 1 ? indiceFotoAtual + 1 : 0;
            setIndiceFotoAtual(novoIndice);
            setFotoSelecionada(fotos[novoIndice]);
        }
    };
    // Navegação por teclado
    const handleKeyDown = (e) => {
        if (!fotoSelecionada)
            return;
        switch (e.key) {
            case 'Escape':
                handleClosePhoto();
                break;
            case 'ArrowLeft':
                handlePreviousPhoto();
                break;
            case 'ArrowRight':
                handleNextPhoto();
                break;
        }
    };
    const marcasFiltradas = marcas?.filter(marca => {
        // Se não há tipo selecionado, não mostrar marcas
        if (!selectedTipoEquipamento)
            return false;
        // Se há modelos carregados, usar o filtro original
        if (modelos && modelos.length > 0) {
            return modelos.some(modelo => modelo.marcaId === marca.id && modelo.tipoEquipamentoId === selectedTipoEquipamento);
        }
        // Se não há modelos ainda, mostrar todas as marcas quando tipo está selecionado
        return true;
    }) || [];
    const modelosFiltrados = modelos?.filter(modelo => (!selectedTipoEquipamento || modelo.tipoEquipamentoId === selectedTipoEquipamento) &&
        (!selectedMarca || modelo.marcaId === selectedMarca)) || [];
    const renderAdicionarNovoModal = () => {
        if (!modalAberto)
            return null;
        const handleConfirmarNovoItem = () => {
            if (!novoItemNome.trim()) {
                error('O nome não pode estar vazio');
                return;
            }
            if (modalAberto === 'tipo') {
                createTipoMutation.mutate(novoItemNome);
            }
            else if (modalAberto === 'marca') {
                createMarcaMutation.mutate(novoItemNome);
            }
            else if (modalAberto === 'modelo') {
                createModeloMutation.mutate({
                    nome: novoItemNome,
                    tipoEquipamentoId: selectedTipoEquipamento,
                    marcaId: selectedMarca,
                });
            }
        };
        return (_jsx(Modal, { isOpen: !!modalAberto, onClose: () => setModalAberto(null), title: `Adicionar Novo ${modalAberto.charAt(0).toUpperCase() + modalAberto.slice(1)}`, children: _jsxs("div", { className: "space-y-4", children: [_jsx(Input, { label: `Nome do Novo ${modalAberto.charAt(0).toUpperCase() + modalAberto.slice(1)}`, value: novoItemNome, onChange: (e) => setNovoItemNome(e.target.value), placeholder: `Digite o nome...` }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { variant: "outline", onClick: () => setModalAberto(null), children: "Cancelar" }), _jsx(Button, { onClick: handleConfirmarNovoItem, isLoading: createTipoMutation.isLoading || createMarcaMutation.isLoading || createModeloMutation.isLoading, children: "Salvar" })] })] }) }));
    };
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-6", children: [renderAdicionarNovoModal(), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [_jsxs("div", { className: "md:col-span-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Cliente *" }), clienteId && selectedCliente ? (
                            // Se clienteId foi passado como prop, mostrar como campo readonly
                            _jsx(Input, { value: selectedCliente.nome, disabled: true, readOnly: true })) : (
                            // Senão, mostrar campo de busca
                            _jsx(SearchInput, { placeholder: "Digite o nome do cliente...", onSearch: searchClientes, onSelect: (result) => {
                                    const cliente = clientes?.find(c => c.id === result.id);
                                    setSelectedCliente(cliente || null);
                                    setFieldValue('clienteId', result.id.toString());
                                }, value: selectedCliente?.nome || '', emptyMessage: "Nenhum cliente encontrado", minSearchLength: 2 }))] }), _jsxs("div", { className: "flex items-end gap-2", children: [_jsxs("div", { className: "flex-grow", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Tipo de Equipamento *" }), _jsx(SearchInput, { placeholder: "Digite o tipo de equipamento...", onSearch: searchTipos, onSelect: (result) => {
                                            const tipo = tiposEquipamento?.find(t => t.id === result.id);
                                            setSelectedTipo(tipo || null);
                                            setSelectedTipoEquipamento(result.id);
                                            setSelectedMarcaObj(null);
                                            setSelectedMarca(undefined);
                                            setSelectedModelo(null);
                                            setFieldValue('modeloId', '');
                                        }, value: selectedTipo?.nome || '', emptyMessage: "Nenhum tipo encontrado", minSearchLength: 1 })] }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => setModalAberto('tipo'), children: _jsx(Plus, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "flex items-end gap-2", children: [_jsxs("div", { className: "flex-grow", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Marca *" }), _jsx(SearchInput, { placeholder: "Digite a marca...", onSearch: searchMarcas, onSelect: (result) => {
                                            const marca = marcas?.find(m => m.id === result.id);
                                            setSelectedMarcaObj(marca || null);
                                            setSelectedMarca(result.id);
                                            setSelectedModelo(null);
                                            setFieldValue('modeloId', '');
                                        }, value: selectedMarcaObj?.nome || '', disabled: !selectedTipoEquipamento, emptyMessage: "Nenhuma marca encontrada", minSearchLength: 1 })] }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => setModalAberto('marca'), disabled: !selectedTipoEquipamento, children: _jsx(Plus, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "md:col-span-2 flex items-end gap-2", children: [_jsxs("div", { className: "flex-grow", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Modelo *" }), _jsx(SearchInput, { placeholder: "Digite o modelo...", onSearch: searchModelos, onSelect: (result) => {
                                            const modelo = modelosFiltrados.find(m => m.id === result.id);
                                            setSelectedModelo(modelo || null);
                                            setFieldValue('modeloId', result.id.toString());
                                        }, value: selectedModelo?.nome || '', disabled: !selectedMarca || !selectedTipoEquipamento, emptyMessage: "Nenhum modelo encontrado", minSearchLength: 1 })] }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => setModalAberto('modelo'), disabled: !selectedMarca || !selectedTipoEquipamento, children: _jsx(Plus, { className: "h-4 w-4" }) })] }), _jsx(Input, { label: "N\u00FAmero de S\u00E9rie", placeholder: "Digite o n\u00FAmero de s\u00E9rie", ...getFieldProps('numeroSerie') }), _jsx(Input, { label: "IMEI", placeholder: "Digite o IMEI (para dispositivos m\u00F3veis)", ...getFieldProps('imei') }), _jsx(Input, { label: "Cor", placeholder: "Digite a cor do equipamento", ...getFieldProps('cor') }), _jsx(Select, { label: "Estado F\u00EDsico *", ...getFieldProps('estadoFisico'), options: estadosFisicos.map(estado => ({
                            value: estado.value,
                            label: estado.label
                        })) }), _jsx("div", { className: "md:col-span-2", children: _jsx(TextArea, { label: "Observa\u00E7\u00F5es", placeholder: "Digite observa\u00E7\u00F5es sobre o equipamento...", rows: 3, ...getFieldProps('observacoes') }) }), _jsxs("div", { className: "md:col-span-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "Fotos do Equipamento" }), fotos.length > 0 && (_jsx("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4 mb-4", children: fotos.map((foto, index) => (_jsxs("div", { className: "relative group", children: [_jsx("img", { src: foto, alt: `Foto ${index + 1}`, className: "w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity", onClick: () => setFotoSelecionada(foto) }), _jsx("button", { type: "button", onClick: () => handleRemovePhoto(index), className: "absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10", children: _jsx(X, { className: "h-3 w-3" }) })] }, index))) })), _jsxs("div", { className: "border-2 border-dashed border-gray-300 rounded-lg p-6 text-center", children: [_jsx("input", { type: "file", id: "photo-upload", accept: "image/*", onChange: handleFileUpload, className: "hidden", disabled: uploadingPhoto }), _jsxs("label", { htmlFor: "photo-upload", className: "cursor-pointer", children: [_jsx(Upload, { className: "h-8 w-8 text-gray-400 mx-auto mb-2" }), _jsx("p", { className: "text-sm text-gray-600", children: uploadingPhoto ? 'Enviando...' : 'Clique para adicionar uma foto' }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "PNG, JPG at\u00E9 5MB" })] })] })] })] }), _jsxs("div", { className: "flex justify-end gap-3 pt-6 border-t border-gray-200", children: [_jsx(Button, { type: "button", variant: "outline", onClick: onCancel, children: "Cancelar" }), _jsx(Button, { type: "submit", isLoading: isSubmitting, leftIcon: equipamento ? undefined : _jsx(QrCode, { className: "h-4 w-4" }), children: equipamento ? 'Atualizar Equipamento' : 'Criar Equipamento' })] }), fotoSelecionada && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50", onClick: () => setFotoSelecionada(null), children: _jsxs("div", { className: "relative max-w-4xl max-h-[90vh] p-4", children: [_jsx("button", { onClick: () => setFotoSelecionada(null), className: "absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10", children: _jsx(X, { className: "h-5 w-5 text-gray-600" }) }), fotos.length > 1 && (_jsxs("div", { className: "absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm z-10", children: [indiceFotoAtual + 1, " de ", fotos.length] })), fotos.length > 1 && indiceFotoAtual > 0 && (_jsx("button", { onClick: (e) => {
                                e.stopPropagation();
                                handlePreviousPhoto();
                            }, className: "absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all z-10", children: _jsx(ChevronLeft, { className: "h-6 w-6 text-gray-800" }) })), fotos.length > 1 && indiceFotoAtual < fotos.length - 1 && (_jsx("button", { onClick: (e) => {
                                e.stopPropagation();
                                handleNextPhoto();
                            }, className: "absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all z-10", children: _jsx(ChevronRight, { className: "h-6 w-6 text-gray-800" }) })), _jsx("img", { src: fotoSelecionada, alt: "Foto ampliada", className: "max-w-full max-h-full object-contain rounded-lg", onClick: (e) => e.stopPropagation() })] }) }))] }));
};
export default EquipamentoForm;
